"use client";

import {
	useState,
	useRef,
	useEffect,
	forwardRef,
	type ForwardedRef
} from "react";
import clsx from "clsx";
import { TextSmall } from "components/TextInfo";
import styles from "./styles.module.scss";
import type { SelectProps } from "./types";

export const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
	{
		id,
		name,
		label,
		value,
		options,
		error,
		errorMessage,
		onChange,
		disabled,
		leading,
		trailing,
		containerClassName,
		placeholder = "Selecione uma opção",
		...props
	},
	ref: ForwardedRef<HTMLDivElement>
) {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((opt) => opt.value === value);

	const handleSelect = (val: string | number) => {
		onChange?.(val);
		setIsOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!wrapperRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<div className={styles.inputWrapper} ref={ref}>
			<label htmlFor={id}>{label}</label>
			<div
				ref={wrapperRef}
				className={clsx(
					styles.container,
					{
						[styles.error]: !!error,
						[styles.disabled]: disabled,
						[styles.open]: isOpen
					},
					containerClassName
				)}
				onClick={() => !disabled && setIsOpen((prev) => !prev)}
			>
				{leading}
				<div className="flex items-center w-full justify-between cursor-pointer">
					<span className={clsx("body", !selectedOption && "text-gray-400")}>
						{selectedOption ? selectedOption.label : placeholder}
					</span>
					{trailing || <div style={{ width: "20px" }} />}
				</div>
				{isOpen && !disabled && (
					<ul className={styles.optionsList}>
						{options.map((opt) => (
							<li key={opt.value}>
								<button
									type="button"
									onClick={() => {
										handleSelect(opt.value);
										setIsOpen((prev) => !prev);
									}}
									className={clsx(styles.optionItem, {
										[styles.selected]: opt.value === value
									})}
								>
									{opt.label}
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
			{error && errorMessage && <TextSmall label={errorMessage} error />}
		</div>
	);
});
